import { getAxiosClient } from '../../client';
import { makeApiRequest } from '../../makeApiRequest';
import type { ApiResponse } from '../../responses';
import type {
    IStatementsService,
    StatementReadOptions,
} from './IStatementsService';
import { StatementsEndpoints } from './statementsEndpoints';
import {
    mapPlausibility,
    mapStatement,
    type PlausibilityWire,
    type StatementWire,
} from './statementsMapping';
import type {
    GetBalanceSheetResponse,
    GetPlausibilityResponse,
    GetProfitAndLossResponse,
} from './responses';

type StatementsServiceOptions = {
    baseUrl: string;
};

/**
 * A body this client could not read as a statement is contract drift, not a
 * figure to guess at. Mirrors `AccountsService`'s `UNMAPPABLE_ACCOUNT`: the
 * screens' error-with-retry state is the correct outcome, and is a great deal
 * safer than rendering a partial statement as though it were the position.
 */
const UNMAPPABLE_STATEMENT: ApiResponse<never> = {
    success: false,
    error: {
        isAxiosError: false,
        status: 500,
        name: '500',
        message: 'The server returned a statement this client cannot read.',
        code: 'UNMAPPABLE_STATEMENT',
    },
};

const DEFAULT_OPTIONS: StatementReadOptions = {
    locale: 'en',
    comparative: 'NONE',
};

/**
 * The live ledger-statements client (bos-api).
 *
 * Every method maps at the boundary via `statementsMapping`, so nothing
 * downstream sees a wire shape — and the server's field names are confined to
 * that one file.
 *
 * What this service deliberately does **not** do:
 *
 * - It does not sum anything. Subtotals, totals, the result-for-period and the
 *   comparative delta all arrive computed, and are passed through untouched.
 * - It does not re-sign the delta. `deltaTetri` is already in the line's natural
 *   terms with the presentation sign excluded.
 * - It does not fetch the layout to enrich a statement. The rendered lines carry
 *   their own label, depth and order; a second structural source could disagree
 *   with the first.
 *
 * `includeZeroLines: true` is sent on every read — see `statementsEndpoints`.
 */
export class StatementsService implements IStatementsService {
    private readonly baseUrl: string;

    constructor({ baseUrl }: StatementsServiceOptions) {
        this.baseUrl = baseUrl;
    }

    private get client() {
        return getAxiosClient({ baseURL: this.baseUrl });
    }

    public async getBalanceSheet(
        asOf: string,
        options: StatementReadOptions = DEFAULT_OPTIONS,
    ): Promise<ApiResponse<GetBalanceSheetResponse>> {
        const result = await makeApiRequest<StatementWire>(
            this.client.get(
                StatementsEndpoints.balanceSheet({
                    asOf,
                    locale: options.locale,
                    comparative: options.comparative,
                    includeZeroLines: true,
                }),
            ),
        );
        if (!result.success) {
            return result;
        }
        const mapped = mapStatement(result.response);
        if (!mapped) {
            return UNMAPPABLE_STATEMENT;
        }
        return { success: true, response: mapped.statement };
    }

    public async getProfitAndLoss(
        fromDate: string,
        toDate: string,
        options: StatementReadOptions = DEFAULT_OPTIONS,
    ): Promise<ApiResponse<GetProfitAndLossResponse>> {
        const result = await makeApiRequest<StatementWire>(
            this.client.get(
                StatementsEndpoints.profitAndLoss({
                    dateFrom: fromDate,
                    dateTo: toDate,
                    locale: options.locale,
                    comparative: options.comparative,
                    includeZeroLines: true,
                }),
            ),
        );
        if (!result.success) {
            return result;
        }
        const mapped = mapStatement(result.response);
        if (!mapped) {
            return UNMAPPABLE_STATEMENT;
        }
        return { success: true, response: mapped.statement };
    }

    /**
     * Advisory notices. A failure here is **not** propagated as a statement
     * failure — the caller treats an unavailable advisory set as "no advisories
     * to show", because warnings must never look like an error and must never
     * block the statement from rendering.
     */
    public async getPlausibility(
        asOf: string,
        options: Pick<StatementReadOptions, 'locale'> = DEFAULT_OPTIONS,
    ): Promise<ApiResponse<GetPlausibilityResponse>> {
        const result = await makeApiRequest<PlausibilityWire>(
            this.client.get(
                StatementsEndpoints.plausibility({
                    asOf,
                    locale: options.locale,
                }),
            ),
        );
        if (!result.success) {
            return result;
        }
        return { success: true, response: mapPlausibility(result.response) };
    }
}
